import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { createReDevicerApi } from '@redevicer/shared'
import { getSiteGateToken } from './siteGate'

/** Development: relative `/api` goes through Vite proxy (LAN-friendly). Override with VITE_API_BASE if needed. */
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'

const api = createReDevicerApi({ baseURL: API_BASE })
api.axios.defaults.withCredentials = true

api.axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const gate = getSiteGateToken()
  if (gate) {
    config.headers = config.headers ?? {}
    config.headers['X-Site-Gate-Token'] = gate
  }
  return config
})

export const authAPI = api.authAPI
export const listingsAPI = api.listingsAPI
export const offersAPI = api.offersAPI
export const paymentsAPI = api.paymentsAPI
export const pricingAPI = api.pricingAPI
export const adminAPI = api.adminAPI
export const imeiAPI = api.imeiAPI

/** IMEI check — optional Bearer token saves report to profile when signed in. */
export function checkImei(
  imei: string,
  token?: string | null,
  service?: string,
  checkoutSessionId?: string | null,
) {
  return imeiAPI.check(imei, token, service, checkoutSessionId)
}

/** @deprecated Use checkImei — auth is optional. */
export function checkImeiAuthenticated(imei: string, token: string) {
  return checkImei(imei, token)
}

export const stripeConnectAPI = api.stripeConnectAPI
export const sellerVerificationAPI = api.sellerVerificationAPI
export const notificationsAPI = api.notificationsAPI
export const aiAssistantAPI = api.aiAssistantAPI
export const shippingAPI = api.shippingAPI
export const addressesAPI = api.addressesAPI

/** @deprecated Use addressesAPI from @redevicer/shared */
export const addressAPI = addressesAPI

/** Browser upload helpers — not used on React Native */
export const uploadAPI = {
  uploadListingImage: (listingId: number, file: File, token: string) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.axios.post(`/upload/listing/${listingId}`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  uploadAvatar: (file: File, token: string) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.axios.post('/upload/avatar', formData, {
      headers: { Authorization: `Bearer ${token}` },
    })
  },

  deleteAvatar: (token: string) =>
    api.axios.delete('/upload/avatar', {
      headers: { Authorization: `Bearer ${token}` },
    }),

  deleteListingImage: (imageId: number, token: string) =>
    api.axios.delete(`/upload/listing/${imageId}`, {
      headers: { Authorization: `Bearer ${token}` },
    }),

  reorderListingImages: (listingId: number, imageOrder: number[], token: string) =>
    api.axios.post(
      `/upload/listing/${listingId}/reorder`,
      { image_order: imageOrder },
      { headers: { Authorization: `Bearer ${token}` } },
    ),

  /**
   * Run the catalog cover cutout on a file and return a Blob URL the seller
   * can preview before saving the listing. Returns ``null`` when the backend
   * couldn't isolate the phone (HTTP 204) — caller falls back to the original
   * photo. Aborting the supplied AbortController cancels stale requests when
   * the seller picks a different cover.
   */
  previewCover: async (
    file: File,
    token: string,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.axios.post('/upload/cover-preview', formData, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      signal,
      validateStatus: (status: number) => status === 200 || status === 204,
    })
    if (res.status === 204) return null
    const blob = res.data as Blob
    if (!blob || blob.size === 0) return null
    return URL.createObjectURL(blob)
  },
}

export const feedbackAPI = {
  submit: (stars: number, message: string, files: File[]) => {
    const formData = new FormData()
    formData.append('stars', String(stars))
    formData.append('message', message)
    for (const file of files) {
      formData.append('files', file)
    }
    return api.axios.post<{ ok: boolean; id: string; files_saved: number }>('/feedback', formData)
  },
}

function requestHadAuthHeader(err: AxiosError): boolean {
  const headers = err.config?.headers
  if (!headers) return false
  const h = headers as Record<string, string | undefined>
  return Boolean(h.Authorization ?? h.authorization)
}

function isPublicAuthFailure(err: AxiosError): boolean {
  const url = err.config?.url ?? ''
  return /\/auth\/(login|register|google|apple|password-reset)/.test(url)
}

api.axios.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: AxiosError<{ detail?: string }>) => {
    if (err.response?.status === 403 && err.response?.data?.detail === 'site_gate_required') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('redevicer:site-gate-required'))
      }
    }
    if (
      err.response?.status === 401 &&
      typeof window !== 'undefined' &&
      requestHadAuthHeader(err) &&
      !isPublicAuthFailure(err)
    ) {
      window.localStorage.removeItem('auth_token')
      window.localStorage.removeItem('user')
      if (!window.location.pathname.includes('login')) {
        window.dispatchEvent(new CustomEvent('redevicer:session-expired'))
      }
    }
    return Promise.reject(err)
  },
)
